import { defineMessages } from 'react-intl';

import type { TimeErrorKey } from './shared.types.js';

/**
 * Default time-validation error messages, shared across time-input components
 * (`DateTimePicker`, `DateTimeRangePicker`, `TimeframeSelector`). Consumers that
 * only need a subset build their own message map by omitting the keys that
 * don't apply to them (see `TIMEFRAMESELECTOR_ERROR_MESSAGES`).
 * @internal
 */
export const TIME_ERROR_MESSAGES = defineMessages<TimeErrorKey>({
  minBoundary: {
    defaultMessage: 'Only times from {min} onwards can be selected.',
    id: 'VrWs4XmGbZ3z7a3Y',
    description:
      'Shown as hint when a min boundary is set, and as error when the selected time is before that boundary.',
  },
  minBoundaryNow: {
    defaultMessage: 'Only times starting from "Now" can be selected.',
    id: 'dBHTi/uZSdvpQ2Zu',
    description:
      "Shown as hint when the min boundary is set to 'Now', and as error when the selected time is before that boundary.",
  },
  maxBoundary: {
    defaultMessage: 'Only times up to {max} can be selected.',
    id: 'qES7K18UnrsWPhwk',
    description:
      'Shown as hint when a max boundary is set, and as error when the selected time is after that boundary.',
  },
  maxBoundaryNowError: {
    defaultMessage:
      'Only times up to "Now" can be selected. Try "Now" or a past time.',
    id: 'KGG/HbTw4ZLbI8vx',
    description:
      "Shown as validation error when the selected time exceeds a max boundary set to 'Now'.",
  },
  minMaxBoundary: {
    defaultMessage: 'Only times from {min} to {max} can be selected.',
    id: '1H2kHk8y+z5PfQEE',
    description:
      'Shown as hint when both boundaries are set, and as error when the selected time falls outside both.',
  },
  invalidOrder: {
    defaultMessage:
      'End time must be after start time. Enter an end time later than {startTime}.',
    id: 'mYn9mGgSX9a2yDnQ',
    description: 'Validation error when the end time is before the start time.',
  },
  singleValueMissing: {
    defaultMessage: 'Please fill in this field.',
    id: 'hIK5810KwQytwP1q',
    description: 'DateTimePicker required value is missing.',
  },
  missingFrom: {
    defaultMessage: 'Please enter a start time.',
    id: 'GUeetpTpusqFkMi+',
    description: 'From value is missing.',
  },
  missingTo: {
    defaultMessage: 'Please enter an end time.',
    id: 'tfHTZkRc7sr/Ypcl',
    description: 'End value is missing.',
  },
  missingFromTo: {
    defaultMessage: 'Please enter a start and end time.',
    id: '4yANH5gdmW1efk0I',
    description: 'Start and end values are missing.',
  },
  valueMissing: {
    defaultMessage: 'Empty timeframe.',
    id: 'QsKlSIxE+Yp58CUG',
    description: 'Timeframe selector required timeframe error hint.',
  },
  invalid: {
    defaultMessage:
      'Invalid time. Expected format: {expectedFormat}, -2h or -2d@d',
    id: 'r/XpT4mat98jCrup',
    description: 'Given value is invalid',
  },
  invalidFrom: {
    defaultMessage:
      'Invalid format for start time. Enter an exact date and time (01/01/2000, 00:00) or a relative expression (now-2h).',
    id: 'p9kOQBgxWYepT8B0',
    description: 'Validation error when the start time input cannot be parsed.',
  },
  invalidTo: {
    defaultMessage:
      'Invalid format for end time. Enter an exact date and time (01/01/2000, 00:00) or a relative expression (now-2h).',
    id: 'HAQQKModsP5sAIpb',
    description: 'Validation error when the end time input cannot be parsed.',
  },
  invalidFromTo: {
    defaultMessage:
      'Invalid formats for start and end time. Enter an exact date and time (01/01/2000, 00:00) or a relative expression (now-2h).',
    id: 'm5UDM9ivObqqdmnm',
    description:
      'Validation error when both start and end time inputs cannot be parsed.',
  },
});
